/**
 * Audit Report Page
 *
 * Displays detailed audit report information for a completed audit session
 * with enhanced hero section and comprehensive metrics visualization.
 */

import type { Community } from '@communities/community/domain';
import { CommunityAuditDetailHeaderSection } from '@internal-audit/components/CommunityAuditDetailHeaderSection';
import { GenerateReportDialog } from '@internal-audit/components/GenerateReportDialog';
import { LabelPerformanceSection } from '@internal-audit/components/LabelPerformanceSection';
import type { GraphQLCommunity, FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useGetAuditReport } from '@internal-audit/hooks/useGetAuditReport';
import { Card, Container, Heading, Section, Text, Button, ButtonType, ButtonSize } from '@soteria/design-system-ve';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { HeroBackground } from '../../core/components';

import * as styles from './AuditReportPage.css';
import { AuditReportPageShimmer } from './AuditReportPageShimmer';

interface ReportData {
  id: string;
  metrics?: {
    overall: {
      precision: number;
      recall: number;
      f1Score: number;
      accuracy: number;
      labelCorrectnessPercentage?: number;
      policyCorrectnessPercentage?: number;
    };
    labelComparison?: {
      exactMatchCount: number;
      exactMatchPercentage: number;
      additionalLabelsCount: number;
      additionalLabelsPercentage: number;
      labelsMissingCount: number;
      labelsMissingPercentage: number;
      bothMissingAndAdditionalCount: number;
      bothMissingAndAdditionalPercentage: number;
    };
    policySummary?: {
      totalPoliciesAssessed: number;
    };
    byPolicy?: unknown[];
    byLabel?: unknown[];
  };
}

// Overall Metrics Component
function OverallMetricsSection({ metrics }: { metrics: NonNullable<ReportData['metrics']>['overall'] }) {
  return (
    <Section background="secondary" spacing="large">
      <div className={styles.sectionContentWrapper}>
        <Heading level="h4">Overall Performance Metrics</Heading>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <Text className={styles.metricLabel}>PRECISION</Text>
            <Text className={`${styles.metricValue} ${styles.precisionValue}`}>{(metrics.precision * 100).toFixed(1)}%</Text>
          </div>
          <div className={styles.metricCard}>
            <Text className={styles.metricLabel}>RECALL</Text>
            <Text className={`${styles.metricValue} ${styles.recallValue}`}>{(metrics.recall * 100).toFixed(1)}%</Text>
          </div>
          <div className={styles.metricCard}>
            <Text className={styles.metricLabel}>F1 SCORE</Text>
            <Text className={`${styles.metricValue} ${styles.f1Value}`}>{(metrics.f1Score * 100).toFixed(1)}%</Text>
          </div>
          <div className={styles.metricCard}>
            <Text className={styles.metricLabel}>ACCURACY</Text>
            <Text className={`${styles.metricValue} ${styles.accuracyValue}`}>{(metrics.accuracy * 100).toFixed(1)}%</Text>
          </div>
          {metrics.labelCorrectnessPercentage !== undefined && (
            <div className={styles.metricCard}>
              <Text className={styles.metricLabel}>LABEL CORRECTNESS</Text>
              <Text className={`${styles.metricValue} ${styles.labelCorrectnessValue}`}>{(metrics.labelCorrectnessPercentage * 100).toFixed(1)}%</Text>
            </div>
          )}
          {metrics.policyCorrectnessPercentage !== undefined && (
            <div className={styles.metricCard}>
              <Text className={styles.metricLabel}>POLICY CORRECTNESS</Text>
              <Text className={`${styles.metricValue} ${styles.policyCorrectnessValue}`}>{(metrics.policyCorrectnessPercentage * 100).toFixed(1)}%</Text>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

// Label Comparison Component
function LabelComparisonSection({ labelComparison }: { labelComparison: NonNullable<ReportData['metrics']>['labelComparison'] }) {
  if (!labelComparison) return null;

  return (
    <Section background="primary" spacing="large">
      <div className={styles.sectionContentWrapper}>
        <Heading level="h4">Label Comparison Analysis</Heading>
        <div className={styles.comparisonGrid}>
          <div className={styles.comparisonCard}>
            <Text className={styles.comparisonLabel}>EXACT MATCHES</Text>
            <Text className={`${styles.comparisonValue} ${styles.exactMatchValue}`}>
              {labelComparison.exactMatchCount} ({(labelComparison.exactMatchPercentage * 100).toFixed(1)}%)
            </Text>
          </div>
          <div className={styles.comparisonCard}>
            <Text className={styles.comparisonLabel}>ADDITIONAL LABELS</Text>
            <Text className={`${styles.comparisonValue} ${styles.additionalLabelsValue}`}>
              {labelComparison.additionalLabelsCount} ({(labelComparison.additionalLabelsPercentage * 100).toFixed(1)}%)
            </Text>
          </div>
          <div className={styles.comparisonCard}>
            <Text className={styles.comparisonLabel}>MISSING LABELS</Text>
            <Text className={`${styles.comparisonValue} ${styles.missingLabelsValue}`}>
              {labelComparison.labelsMissingCount} ({(labelComparison.labelsMissingPercentage * 100).toFixed(1)}%)
            </Text>
          </div>
          <div className={styles.comparisonCard}>
            <Text className={styles.comparisonLabel}>BOTH MISSING & ADDITIONAL</Text>
            <Text className={`${styles.comparisonValue} ${styles.bothMissingAdditionalValue}`}>
              {labelComparison.bothMissingAndAdditionalCount} ({(labelComparison.bothMissingAndAdditionalPercentage * 100).toFixed(1)}%)
            </Text>
          </div>
        </div>
      </div>
    </Section>
  );
}

// Policy Sections Component
function PolicySections({ metrics }: { metrics: ReportData['metrics'] }) {
  return (
    <>
      <Section background="primary" spacing="large">
        <div className={styles.sectionContentWrapper}>
          <Heading level="h4">Policy Summary (External QA)</Heading>
          <Text>This is a test - Policy Summary section is now visible! Total policies assessed: {metrics?.policySummary?.totalPoliciesAssessed || 0}</Text>
        </div>
      </Section>

      <Section background="secondary" spacing="large">
        <div className={styles.sectionContentWrapper}>
          <Heading level="h4">Policy Performance Metrics (External QA)</Heading>
          <Text>This is a test - Policy Metrics section is now visible! Policy count: {metrics?.byPolicy?.length || 0}</Text>
        </div>
      </Section>
    </>
  );
}

// Label Performance Component
function LabelPerformanceWrapper({ byLabel }: { byLabel?: unknown[] }) {
  if (!byLabel || byLabel.length === 0) return null;

  return (
    <Section background="secondary" spacing="large">
      <div className={styles.sectionContentWrapper}>
        <LabelPerformanceSection labelMetrics={byLabel as never} />
      </div>
    </Section>
  );
}

// Report Item Component
function ReportItem({ report }: { report: ReportData }) {
  if (!report.metrics) {
    return null;
  }

  return (
    <div key={report.id}>
      <OverallMetricsSection metrics={report.metrics.overall} />
      <LabelComparisonSection labelComparison={report.metrics.labelComparison} />
      <PolicySections metrics={report.metrics} />
      <LabelPerformanceWrapper byLabel={report.metrics.byLabel} />
    </div>
  );
}

// Loading Component
function LoadingState() {
  return (
    <div>
      <CommunityAuditDetailHeaderSection isLoading auditItems={[]} community={null} session={null} />
      <AuditReportPageShimmer />
    </div>
  );
}

// Error Component
function ErrorState({ errors, onRetry, onBack }: { errors: { userMessage?: string; code?: string }[]; onRetry: () => void; onBack: () => void }) {
  return (
    <Container>
      <Section spacing="large">
        <Card className={styles.errorCard}>
          <Section spacing="medium">
            <Heading level="h2">Error Loading Report</Heading>
            <Text>{errors[0]?.userMessage || 'Failed to load audit report.'}</Text>
            <Text>Error Code: {errors[0]?.code || 'UNKNOWN'}</Text>
            <div className={styles.errorActions}>
              <Button buttonSize={ButtonSize.Default} buttonType={ButtonType.Tertiary} label="Retry" onClick={onRetry} />
              <Button buttonSize={ButtonSize.Default} buttonType={ButtonType.Tertiary} label="Back" onClick={onBack} />
            </div>
          </Section>
        </Card>
      </Section>
    </Container>
  );
}

// No Reports Component
function NoReportsState({
  community,
  session,
  communityId,
  sessionId,
}: {
  community: Community | GraphQLCommunity | null;
  session: FlexibleAuditSession | null;
  communityId: string;
  sessionId: string;
}) {
  return (
    <>
      <div>
        <div>
          <CommunityAuditDetailHeaderSection auditItems={[]} community={community} isLoading={false} session={session} />
        </div>
        <div className={styles.contentSection}>
          <div className={styles.contentContainer} />
        </div>
      </div>
      <GenerateReportDialog isOpen communityId={communityId} sessionId={sessionId} onClose={() => {}} />
    </>
  );
}

// Not Found Component
function NotFoundState({ title, message }: { title: string; message: string }) {
  return (
    <Container>
      <Section spacing="large">
        <Card className={styles.errorCard}>
          <Section spacing="medium">
            <Heading level="h2">{title}</Heading>
            <Text>{message}</Text>
          </Section>
        </Card>
      </Section>
    </Container>
  );
}

// Main Component
export function AuditReportPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const { communityId, sessionId } = useParams<{ communityId: string; sessionId: string }>();
  const { reports, community, session, loading, errors } = useGetAuditReport(communityId, sessionId);

  const handleBackClick = () => navigate(-1);
  const handleRetry = () => window.location.reload();

  if (loading) return <LoadingState />;
  if (errors.length > 0) return <ErrorState errors={errors} onBack={handleBackClick} onRetry={handleRetry} />;
  if (reports.length === 0) {
    return <NoReportsState community={community} communityId={communityId || ''} session={session} sessionId={sessionId || ''} />;
  }

  if (!community) return <NotFoundState message="The requested community could not be found." title="Community Not Found" />;
  if (!session) return <NotFoundState message="The requested audit session could not be found." title="Session Not Found" />;

  return (
    <div>
      <HeroBackground imageUrl={(community as { superHeroArtUrl?: string })?.superHeroArtUrl} />
      <div>
        <CommunityAuditDetailHeaderSection auditItems={[]} community={community} isLoading={false} session={session} />
      </div>
      <div className={styles.contentSection}>
        <div className={styles.contentContainer}>
          <Section spacing="large">
            <Heading level="h2">Audit Reports ({reports.length})</Heading>
            {reports.map(report => (
              <ReportItem key={report.id} report={report} />
            ))}
            {reports.length > 0 && (
              <Section spacing="large">
                <div className={styles.reportFooter}>
                  <Text className={styles.footerText}>
                    Created {new Date(reports[0].createdAt).toLocaleDateString('en-CA')} by {reports[0].createdBy}
                  </Text>
                </div>
              </Section>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
